<script setup lang="ts">
import IllustrativePenIcon from '@/assets/icons/IllustrativePenIcon.vue';
import { useVisualViewportBounds } from '@/composables/useVisualViewportBounds';
import NativeDialog from '@/core/dialogsystem/NativeDialog.vue';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    accessibleLabel: string;
    collapsedLabel: string;
    /** False where the host owns closing (a dialog), so no collapsed composer is rendered at all. */
    collapsible?: boolean;
    /** Sizes the desktop panel to its content instead of the general quick-entry minimum height. */
    compact?: boolean;
    expanded: boolean;
    /** Drops the drop shadow: inside a scrolling container it is clipped rather than cast. */
    flat?: boolean;
    mobileViewport?: boolean;
  }>(),
  {
    collapsible: true,
    compact: false,
    flat: false,
    mobileViewport: false,
  },
);

/** Breathing room kept between an in-flow panel's bottom edge and the bottom of the viewport. */
const VIEWPORT_GAP = '1rem';
/** A centred dialog's top and bottom margin combined; it cannot be measured, so it is stated. */
const DIALOG_MARGIN = '4rem';

const emit = defineEmits<{
  expand: [];
  opened: [];
  'request-collapse': [event?: Event];
}>();

const desktopExpanded = computed(() => props.expanded && !props.mobileViewport);

const boxRef = useTemplateRef<HTMLElement>('boxRef');
const availableHeight = ref<string>();

// The panel may not outgrow the space below it, otherwise the action row ends up off screen. A
// centred dialog cannot be measured — its offset follows its own height — so it takes the static
// centred cap; anywhere in page flow the offset is read from the box itself, which is exact in each
// of the inline hosts.
const measureAvailableHeight = (): void => {
  const box = boxRef.value;
  if (!box || !desktopExpanded.value) return;
  const next = box.closest('dialog')
    ? `calc(100dvh - ${DIALOG_MARGIN})`
    : // clamped: a negative top (panel scrolled past the viewport) would otherwise buy height it
      // does not have
      `calc(100dvh - ${Math.max(0, Math.round(box.getBoundingClientRect().top))}px - ${VIEWPORT_GAP})`;
  if (next !== availableHeight.value) availableHeight.value = next;
};

watch(desktopExpanded, async (expanded) => {
  if (!expanded) return;
  await nextTick();
  measureAvailableHeight();
});

onMounted(() => {
  measureAvailableHeight();
  window.addEventListener('resize', measureAvailableHeight);
  // Scrolling the page moves the panel up — once the filters above it are gone that space is the
  // composer's to take. `capture` because scroll does not bubble, so this also catches the inner
  // scroll containers the composer can be hosted in.
  document.addEventListener('scroll', measureAvailableHeight, { capture: true, passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', measureAvailableHeight);
  document.removeEventListener('scroll', measureAvailableHeight, { capture: true });
});

const mobileDialogRef = useTemplateRef<InstanceType<typeof NativeDialog>>('mobileDialogRef');
const { fixedViewportStyle } = useVisualViewportBounds();

watch(
  () => props.expanded && props.mobileViewport,
  async (showMobileViewport) => {
    if (!showMobileViewport) {
      mobileDialogRef.value?.close();
      return;
    }

    await nextTick();
    if (!props.expanded || !props.mobileViewport || mobileDialogRef.value?.element?.open) return;
    mobileDialogRef.value?.showModal();
    await nextTick();
    emit('opened');
  },
  { immediate: true },
);
</script>

<template>
  <!-- one box for both states: it keeps its background, shadow and ring across the whole morph, so
       the collapsed composer is never a second box showing through -->
  <div
    ref="boxRef"
    :style="{ '--composer-available': availableHeight }"
    class="group relative grid w-full bg-surface-primary motion-panel-box"
    :class="
      desktopExpanded
        ? ['rounded-group', !props.flat && 'shadow-floating-panel']
        : ['rounded-quick-entry-collapsed', !props.flat && 'shadow-quick-entry']
    "
  >
    <div
      class="pointer-events-none absolute inset-0 border-3 motion-panel-box"
      :class="
        desktopExpanded
          ? 'rounded-group border-focus shadow-tint-inner'
          : 'rounded-quick-entry-collapsed border-transparent group-hover:border-focus group-hover:shadow-quick-entry-hover'
      "
      aria-hidden="true"
    />

    <button
      v-if="props.collapsible"
      type="button"
      :inert="props.expanded"
      class="col-start-1 row-start-1 flex h-quick-entry-collapsed w-full items-center gap-md rounded-quick-entry-collapsed px-md py-md text-left outline-inner motion-panel-box"
      :class="props.expanded && 'opacity-0'"
      :aria-label="props.accessibleLabel"
      @click="emit('expand')"
    >
      <span class="flex size-icon-button shrink-0 items-center justify-center rounded-button bg-surface-secondary">
        <IllustrativePenIcon class="size-icon-sm" />
      </span>
      <span class="min-w-0 flex-1 truncate text-base text-tertiary">
        {{ props.collapsedLabel }}
      </span>
    </button>

    <Transition
      enter-from-class="motion-panel-collapsed"
      enter-active-class="motion-panel-reveal"
      leave-active-class="motion-panel-reveal"
      leave-to-class="motion-panel-collapsed"
    >
      <div v-if="desktopExpanded" class="col-start-1 row-start-1 grid min-h-0 min-w-0 grid-rows-[1fr]">
        <!-- the clip's own min-height is what the 0fr track resolves to while morphing, so it has to
             stay the collapsed height; the settled minimum lives one level down -->
        <div class="flex min-h-quick-entry-collapsed min-w-0 flex-col max-h-[var(--composer-available)]">
          <div class="flex min-w-0 flex-col p-md" :class="props.compact ? 'min-h-0' : 'min-h-quick-entry-expanded'">
            <slot></slot>
          </div>
        </div>
      </div>
    </Transition>
  </div>

  <NativeDialog
    v-if="props.expanded && props.mobileViewport"
    ref="mobileDialogRef"
    class="fixed inset-0 m-0 h-dvh max-h-none w-screen max-w-none overflow-hidden rounded-none border-0 bg-surface-primary p-0 outline-none"
    :aria-label="props.accessibleLabel"
    :close-on-backdrop-click="false"
    :close-on-cancel="false"
    @backdrop-click="emit('request-collapse', $event)"
    @cancel="emit('request-collapse', $event)"
  >
    <main
      class="fixed flex min-h-0 flex-col overflow-hidden bg-surface-primary px-md pb-mobile-safe-bottom-md pt-mobile-safe-top-md"
      :style="fixedViewportStyle"
    >
      <div
        class="relative flex min-h-0 flex-1 flex-col rounded-group bg-surface-primary p-md"
        :class="!props.flat && 'shadow-floating-panel'"
      >
        <div
          class="pointer-events-none absolute inset-0 rounded-group border-3 border-focus shadow-tint-inner"
          aria-hidden="true"
        />
        <slot></slot>
      </div>
    </main>
  </NativeDialog>
</template>
